import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import BWButton from '../BWButton';
import PropTypes from 'prop-types';
import CheckAccess from 'navigation/CheckAccess';
import _ from 'lodash';
import styled from 'styled-components';
import { InputNumber, Pagination } from 'antd';
import viVN from 'antd/lib/locale/vi_VN';
import { Empty } from 'antd';
import TrTable from './element/TrTable';
import { Tooltip } from 'antd';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { defaultActionLabel, PortalIcon, normalizePortalText } from 'utils/baochauTheme';
import { useAuth } from 'context/AuthProvider';
import {
  getDataTableColumns,
  getColumnPreferenceKey,
  getDefaultTablePreferences,
  loadTablePreferences,
  saveTablePreferences,
  setTableColumnWidth,
  toggleTableColumn,
} from './tablePreferences';

const DelListStyled = styled.span`
  display: ${(props) => (props?.hidden ? 'none' : '')};
  cursor: pointer;
  b {
    color: red;
  }
`;

const InputNumberStyled = styled(InputNumber)`
  margin-right: 6px;
  .ant-input-number-handler-wrap {
    display: none !important;
  }
`;

const Wrapper = styled.div`
  position: relative;

  @keyframes loading {
    to {
      background-position-x: -30%;
    }
  }
  .loader {
    background-color: #ededed;
    height: 18px;
    border-radius: 7px;
    width: 100%;
  }
  td .loader {
    background-color: #ededed;
    background: linear-gradient(
        100deg,
        rgba(255, 255, 255, 0) 40%,
        rgba(255, 255, 255, 0.5) 50%,
        rgba(255, 255, 255, 0) 60%
      )
      #ededed;
    background-size: 200% 100%;
    background-position-x: 180%;
    animation: 1s loading ease-in-out infinite;
  }

  .bw_resizable_table {
    width: max-content;
    min-width: 100%;
    table-layout: auto;
  }

  .bw_resizable_header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 24px;
    padding-right: 12px;
  }

  .bw_resize_handle {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 2;
    width: 12px;
    border: 0;
    background: transparent;
    cursor: col-resize;
    touch-action: none;
  }

  .bw_resize_handle::after {
    content: '';
    position: absolute;
    top: 2px;
    right: 0;
    bottom: 2px;
    width: 1px;
    background: rgba(255, 255, 255, 0.45);
  }

  .bw_resize_handle:hover::after,
  .bw_resize_handle:focus-visible::after {
    width: 2px;
    background: #ffffff;
  }

  .bw_column_settings {
    position: absolute;
    z-index: 10;
    top: 48px;
    right: 8px;
    width: 280px;
    max-height: 360px;
    overflow: auto;
    padding: 12px;
    border: 1px solid #dce3ec;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.16);
  }

  .bw_column_settings_title,
  .bw_column_settings_footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .bw_column_settings_title {
    margin-bottom: 8px;
    color: #111827;
    font-weight: 700;
  }

  .bw_column_settings_item {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 34px;
    border-bottom: 1px solid #eef1f5;
    color: #344054;
  }

  .bw_column_settings_footer {
    justify-content: flex-end;
    padding-top: 10px;
  }
`;

const DataTable = ({
  // show left
  loading,
  title,
  leadingColumns = [],
  columns,
  data = [],
  styleAction,
  actions,
  toolbarActions,
  emptyContent,
  numberedPagination = false,
  fieldCheck,
  noSelect,
  noPaging,
  defaultDataSelect,
  showBulkButton,
  styleTitle,
  page,
  totalPages,
  totalItems,
  itemsPerPage,
  onChangeSelect = () => {},
  onChangePage,
  hiddenRowSelect,
  handleBulkAction = () => {},
  hiddenDeleteClick,
  getChildren,
  parentField,
  customSumRow = null,
  secondaryAction = undefined,
  uniqueSelect = false,
  ownId = '',
  isOrderProductList,
  refreshFlag,
  detailTr,
  onRowClick,
  hiddenHead = false,
  hideRecordCount = false,
  plain = false,
  preserveSelectionOnEmptyData = false,
  columnPreferenceKey,
}) => {
  const { user } = useAuth();
  const visibleLeadingColumns = useMemo(
    () => getDataTableColumns(leadingColumns).filter((column) => !column.hidden),
    [leadingColumns],
  );
  const dataColumns = useMemo(() => getDataTableColumns(columns), [columns]);
  const [currentPage, setCurrentPage] = useState(parseInt(page));
  const [dataSelect, setDataSelect] = useState(defaultDataSelect ?? []);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const resizeCleanupRef = useRef(null);
  const preferenceStorageKey = columnPreferenceKey
    ? `baochau.data-table.${columnPreferenceKey}.${user?.user_name || user?.username || 'anonymous'}`
    : null;
  const [tablePreferences, setTablePreferences] = useState(() =>
    loadTablePreferences(window.localStorage, preferenceStorageKey, dataColumns),
  );
  const latestPreferencesRef = useRef(tablePreferences);

  useEffect(() => {
    const nextPreferences = loadTablePreferences(window.localStorage, preferenceStorageKey, dataColumns);
    latestPreferencesRef.current = nextPreferences;
    // Several form tables build their column array during render. In that
    // case `dataColumns` has a new reference on every render; only update
    // state when the effective preferences actually changed.
    setTablePreferences((currentPreferences) =>
      _.isEqual(currentPreferences, nextPreferences) ? currentPreferences : nextPreferences,
    );
  }, [preferenceStorageKey, dataColumns]);

  useEffect(() => {
    latestPreferencesRef.current = tablePreferences;
  }, [tablePreferences]);

  useEffect(
    () => () => {
      resizeCleanupRef.current?.();
    },
    [],
  );

  const displayColumns = useMemo(() => {
    const hiddenKeys = new Set(tablePreferences.hiddenKeys);
    const resizedKeys = new Set(tablePreferences.resizedKeys || []);
    return dataColumns
      .map((column, index) => {
        const preferenceKey = getColumnPreferenceKey(column, index);
        const width = tablePreferences.widths[preferenceKey];
        const isResized = resizedKeys.has(preferenceKey);
        return {
          ...column,
          preferenceKey,
          isResized,
          hidden: column.hidden || (Boolean(columnPreferenceKey) && hiddenKeys.has(preferenceKey)),
          styleHeader: columnPreferenceKey && isResized
            ? { ...column.styleHeader, width, minWidth: width, maxWidth: width }
            : column.styleHeader,
          style: columnPreferenceKey && isResized
            ? typeof column.style === 'function'
              ? (row) => ({ ...column.style(row), width, minWidth: width, maxWidth: width })
              : { ...column.style, width, minWidth: width, maxWidth: width }
            : column.style,
        };
      })
      .filter((column) => !column.hidden);
  }, [columnPreferenceKey, dataColumns, tablePreferences]);

  const commitTablePreferences = useCallback(
    (nextPreferences, persist = true) => {
      latestPreferencesRef.current = nextPreferences;
      setTablePreferences(nextPreferences);
      if (persist) {
        saveTablePreferences(window.localStorage, preferenceStorageKey, dataColumns, nextPreferences);
      }
    },
    [dataColumns, preferenceStorageKey],
  );

  const handleResetColumns = useCallback(() => {
    commitTablePreferences(getDefaultTablePreferences(dataColumns));
  }, [dataColumns, commitTablePreferences]);

  const handleToggleColumn = useCallback(
    (key) => commitTablePreferences(toggleTableColumn(dataColumns, latestPreferencesRef.current, key)),
    [dataColumns, commitTablePreferences],
  );

  const startColumnResize = useCallback(
    (event, column) => {
      event.preventDefault();
      event.stopPropagation();
      resizeCleanupRef.current?.();
      const startX = event.clientX;
      const key = column.preferenceKey;
      const measuredWidth = event.currentTarget?.closest('th')?.getBoundingClientRect()?.width;
      const startWidth =
        Number.isFinite(measuredWidth) && measuredWidth > 0
          ? measuredWidth
          : latestPreferencesRef.current.widths[key];
      const handlePointerMove = (moveEvent) => {
        const nextPreferences = setTableColumnWidth(
          dataColumns,
          latestPreferencesRef.current,
          key,
          startWidth + moveEvent.clientX - startX,
        );
        commitTablePreferences(nextPreferences, false);
      };
      const cleanup = () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
        document.body.style.cursor = '';
        resizeCleanupRef.current = null;
      };
      const handlePointerUp = () => {
        saveTablePreferences(window.localStorage, preferenceStorageKey, dataColumns, latestPreferencesRef.current);
        cleanup();
      };
      document.body.style.cursor = 'col-resize';
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      resizeCleanupRef.current = cleanup;
    },
    [dataColumns, commitTablePreferences, preferenceStorageKey],
  );

  const handleColumnResizeKeyDown = useCallback(
    (event, column) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;

      event.preventDefault();
      event.stopPropagation();
      const measuredWidth = event.currentTarget?.closest('th')?.getBoundingClientRect()?.width;
      const currentWidth =
        Number.isFinite(measuredWidth) && measuredWidth > 0
          ? measuredWidth
          : latestPreferencesRef.current.widths[column.preferenceKey];
      const resizeStep = event.shiftKey ? 80 : 24;
      const direction = event.key === 'ArrowLeft' ? -1 : 1;
      const nextPreferences = setTableColumnWidth(
        dataColumns,
        latestPreferencesRef.current,
        column.preferenceKey,
        currentWidth + direction * resizeStep,
      );
      commitTablePreferences(nextPreferences);
    },
    [commitTablePreferences, dataColumns],
  );

  //data first
  const queryPatch = new URLSearchParams(useLocation().search);
  const search = queryPatch.get('search');

  useEffect(() => {
    if (refreshFlag || (!preserveSelectionOnEmptyData && data.length === 0)) {
      setDataSelect((currentSelection) => (currentSelection.length ? [] : currentSelection));
    }
  }, [refreshFlag, data, preserveSelectionOnEmptyData]);

  const totalChecked = useMemo(() => {
    const selectableRows = (data ?? []).filter((row) => typeof hiddenRowSelect !== 'function' || !hiddenRowSelect(row));
    return selectableRows.length > 0 && selectableRows.every((row) =>
      dataSelect.some((selected) => {
        if (fieldCheck) {
          return String(selected[fieldCheck]) === String(row[fieldCheck]);
        } else {
          return _.isEqual(selected, row);
        }
      }),
    );
  }, [data, dataSelect, fieldCheck, hiddenRowSelect]);

  const rowAction = useMemo(() => {
    return (actions ?? []).filter((p) => !p.globalAction && !Boolean(typeof p.hidden !== 'function' && p.hidden));
  }, [actions]);

  const actionColumnWidth = useMemo(() => {
    if (!rowAction.length) return 0;
    const maxVisibleActionCount = data?.length
      ? Math.max(
          ...data.map(
            (row) =>
              rowAction.filter((action) =>
                typeof action.hidden === 'function' ? !action.hidden(row) : !action.hidden,
              ).length,
          ),
          1,
        )
      : rowAction.length;

    return Math.max(132, maxVisibleActionCount * 34 + 16);
  }, [data, rowAction]);

  const globalActions = useMemo(() => {
    return (actions ?? []).filter((p) => p.globalAction && !p.hidden);
  }, [actions]);

  const colSpan = useMemo(() => {
    const spanSelect = noSelect ? 0 : 1;
    const spanIndex = 1;
    const spanRowAction = Boolean(rowAction.length) ? 1 : 0;
    return spanSelect + spanIndex + spanRowAction + visibleLeadingColumns.length + displayColumns.length;
  }, [displayColumns, noSelect, rowAction, visibleLeadingColumns]);

  const renderRowAction = useCallback(
    (valueRender, keyRender) => {
      return (rowAction ?? [])?.map((action) => {
        const { hidden, disabled, permission, onClick, color, icon, title, render, any, requireAssigned } = action;
        const isHidden = Boolean(typeof hidden === 'function' ? hidden(valueRender) : hidden);

        // Do not leave hidden actions in the DOM. The global `.bw_btn_table`
        // style uses `display: inline-flex !important`, which otherwise makes a
        // hidden action reappear as a faded, disabled button.
        if (isHidden) {
          return null;
        }

        const isDisabled = isHidden ? true : typeof disabled === 'function' ? disabled?.(valueRender) : disabled;
        const resolvedPermission = typeof permission === 'function' ? permission(valueRender) : permission;

        if (typeof render === 'function') {
          const actionNode = render(valueRender, keyRender, {
            disabled: isDisabled,
            hidden: isHidden,
          });

          return isHidden || !actionNode ? null : (
            <CheckAccess
              key={`${String(title || icon)}-${keyRender}`}
              permission={resolvedPermission}
              any={any}
              requireAssigned={requireAssigned}>
              {actionNode}
            </CheckAccess>
          );
        }

        const resolvedIcon = typeof icon === 'function' ? icon(valueRender) : icon;
        const actionLabel = title || defaultActionLabel(resolvedIcon);
        const actionClassName = `bw_btn_table bw_${color}`;
        const actionButton = (
          <button
            type='button'
            disabled={isDisabled}
            onClick={() => (isDisabled ? {} : onClick?.(valueRender, keyRender))}
            style={{
              marginRight: '2px',
              opacity: `${isHidden ? '0.25' : '1'}`,
              cursor: `${isHidden ? 'default' : 'pointer'}`,
              display: isHidden ? 'none' : '',
            }}
            title={actionLabel}
            aria-label={actionLabel}
            className={actionClassName}>
            <PortalIcon icon={resolvedIcon} className='bw_table_action_icon' />
          </button>
        );
        return action.tips ? (
          <CheckAccess
            key={`${String(actionLabel || icon)}-${keyRender}`}
            permission={resolvedPermission}
            any={any}
            requireAssigned={requireAssigned}>
            <Tooltip title={isHidden ? '' : action.tips}>
              <span>{actionButton}</span>
            </Tooltip>
          </CheckAccess>
        ) : (
          <CheckAccess
            key={`${String(actionLabel || icon)}-${keyRender}`}
            permission={resolvedPermission}
            any={any}
            requireAssigned={requireAssigned}>
            {actionButton}
          </CheckAccess>
        );
      });
    },
    [rowAction],
  );

  const totalShowRecord = useMemo(() => {
    if (data.length < itemsPerPage) {
      return data.length;
    } else if (itemsPerPage > totalItems) {
      return totalItems;
    } else {
      return itemsPerPage;
    }
  }, [data, itemsPerPage, totalItems]);

  const shouldShowPaging = useMemo(() => {
    return !noPaging && !loading && Number(totalItems) > 0 && Number(totalPages) > 0 && data?.length > 0;
  }, [noPaging, loading, totalItems, totalPages, data]);

  const renderData = useCallback(
    (valueRender, keyRender) => {
      let _dataSelect = [...dataSelect];
      const findIndex = _dataSelect.findIndex((o) => {
        if (fieldCheck) {
          return String(o[fieldCheck]) === String(valueRender[fieldCheck]);
        } else {
          return _.isEqual(o, valueRender);
        }
      });

      const flag = findIndex >= 0;
      const indexValue = ((parseInt(page) || 1) - 1) * (parseInt(itemsPerPage) || data.length || 1) + keyRender + 1;
      const rowKey =
        (fieldCheck && valueRender?.[fieldCheck]) ??
        valueRender?.id ??
        valueRender?.value ??
        valueRender?.type ??
        keyRender;
      return (
        <TrTable
          key={`${ownId || 'data-table'}-${rowKey}-${keyRender}`}
          colSpan={colSpan}
          keyRender={keyRender}
          flag={flag}
          noSelect={noSelect}
          hiddenRowSelect={hiddenRowSelect}
          indexValue={indexValue}
          _dataSelect={_dataSelect}
          setDataSelect={setDataSelect}
          leadingColumns={visibleLeadingColumns}
          columns={displayColumns}
          valueRender={valueRender}
          findIndex={findIndex}
          rowAction={rowAction}
          renderRowAction={renderRowAction}
          getChildren={getChildren}
          parentField={parentField}
          onChangeSelect={onChangeSelect}
          uniqueSelect={uniqueSelect}
          detailTr={detailTr}
          onRowClick={onRowClick}
        />
      );
    },
    [
      colSpan,
      data.length,
      dataSelect,
      detailTr,
      displayColumns,
      fieldCheck,
      getChildren,
      hiddenRowSelect,
      itemsPerPage,
      noSelect,
      onChangeSelect,
      onRowClick,
      ownId,
      page,
      parentField,
      renderRowAction,
      rowAction,
      search,
      uniqueSelect,
      visibleLeadingColumns,
    ],
  );

  const handleCheckAll = useCallback(() => {
    const _dataSelect = [...dataSelect];
    if (totalChecked) {
      for (let i of data) {
        const findIndex = _dataSelect.findIndex((o) => {
          if (fieldCheck) {
            return String(o[fieldCheck]) === String(i[fieldCheck]);
          } else {
            return _.isEqual(o, i);
          }
        });
        if (findIndex >= 0) {
          _dataSelect.splice(findIndex, 1);
        }
        setDataSelect(_dataSelect);
      }
    } else {
      for (let i of data) {
        if (!hiddenRowSelect?.(i)) {
          const findIndex = _dataSelect.findIndex((o) => {
            if (fieldCheck) {
              return String(o[fieldCheck]) === String(i[fieldCheck]);
            } else {
              return _.isEqual(o, i);
            }
          });
          if (findIndex < 0) {
            _dataSelect.push(i);
          }
          setDataSelect(_dataSelect);
        }
      }
    }
  }, [totalChecked, dataSelect, data, fieldCheck, hiddenRowSelect]);

  useEffect(() => {
    onChangeSelect?.(dataSelect);
  }, [onChangeSelect, dataSelect]);

  useEffect(() => {
    setCurrentPage(totalPages ? parseInt(page) : totalPages);
  }, [page, totalPages]);

  const handeChangePage = useCallback(() => {
    if (parseInt(currentPage) !== parseInt(page)) onChangePage(currentPage);
  }, [currentPage, onChangePage, page]);

  const jsx_tbody = () => {
    const sumColumnIndexs = customSumRow?.reduce((acc, item) => {
      return acc.concat(
        item.colSpan > 1 ? Array.from({ length: item.colSpan }, (_, index) => index + item.index) : [item.index],
      );
    }, []);

    const jsx =
      data?.length > 0 ? (
        <tbody>
          {data?.map((value, key) => renderData(value, key))}
          {customSumRow ? (
            <tr className='bw_data_table_summary'>
              {!noSelect && <td></td>}
              {visibleLeadingColumns.map((column, index) => (
                <td key={`leading-summary-${column?.key || column?.accessor || index}`}></td>
              ))}
              <td></td>
              {displayColumns
                ?.reduce((acc, item, _index) => {
                  const sumRecord = customSumRow.find((_) =>
                    _.key ? _.key === item.preferenceKey : _.index === _index + 1,
                  );
                  let currentValue = [];
                  if (sumRecord) {
                    currentValue.push(
                      <td key={`sum-${_index}`} colSpan={sumRecord.colSpan ?? 1}>
                        <div
                          style={sumRecord.style ? { ...sumRecord.style, fontWeight: 'bold' } : { fontWeight: 'bold' }}>
                          {sumRecord?.value ?? sumRecord?.formatter(data)}
                        </div>
                      </td>,
                    );
                  } else if (!sumColumnIndexs?.includes(_index + 1)) {
                    currentValue.push(<td key={`empty-${_index}`}></td>);
                  }
                  return acc.concat(currentValue);
                }, [])}
              {Boolean(rowAction.length) && <td key='row-action-summary'></td>}
            </tr>
          ) : null}
        </tbody>
      ) : (
        <tbody>
          <tr>
            <td colSpan={colSpan}>
              {emptyContent ?? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='Không có dữ liệu' />}
            </td>
          </tr>
        </tbody>
      );

    return jsx;
  };

  return (
    <React.Fragment>
      <Wrapper
        style={
          actionColumnWidth
            ? {
                '--bw-action-column-width': `${actionColumnWidth}px`,
              }
            : undefined
        }
        className={[
          plain ? null : 'bw_box_card',
          'bw_data_grid_standard',
        ]
          .filter(Boolean)
          .join(' ') || undefined}>
        {!hiddenHead && (title || Boolean(dataSelect.length) || Boolean(globalActions.length) || toolbarActions || columnPreferenceKey) && (
          <div
            className={
              isOrderProductList
                ? 'bw_row bw_align_items_center bw_data_table_toolbar'
                : 'bw_row bw_mt_2 bw_mb_2 bw_align_items_center bw_data_table_toolbar'
            }>
            <div className='bw_col_6' style={styleTitle}>
              {typeof title === 'string' ? normalizePortalText(title) : title}
              {Boolean(dataSelect.length) > 0 && (
                <div className='bw_show_record'>
                  <p className='bw_choose_record'>
                    {showBulkButton && (
                    <button
                      className='bw_btn bw_btn_success'
                      style={{ marginRight: '8px' }}
                      type='button'
                      onClick={() => {
                        handleBulkAction(dataSelect);
                      }}>
                        <PortalIcon icon='fi fi-rr-add' className='bw_btn_icon' style={{ lineHeight: 0 }} /> Chọn
                      </button>
                    )}
                    <b>{dataSelect.length}</b> đang chọn{' '}
                    <button
                      id={`data-table-select${ownId ? `-${ownId}` : ''}`}
                      type='button'
                      className='bw_table_inline_action'
                      onClick={() => {
                        setDataSelect([]);
                      }}>
                      Bỏ chọn
                    </button>{' '}
                    {!isOrderProductList && (
                      <DelListStyled
                        id='trigger-delete'
                        hidden={hiddenDeleteClick}
                        className='bw_red bw_delete'
                        onClick={() => {
                          handleBulkAction && handleBulkAction(dataSelect, 'delete');
                        }}>
                        | <b>Xoá tất cả</b>
                      </DelListStyled>
                    )}
                  </p>
                </div>
              )}
            </div>
            <div className='bw_col_6 bw_flex bw_justify_content_right bw_btn_group' style={styleAction}>
              {toolbarActions}
              {columnPreferenceKey && (
                <button
                  type='button'
                  className='bw_btn bw_btn_outline bw_btn_icon'
                  title='Tùy chỉnh cột'
                  aria-label='Tùy chỉnh cột'
                  onClick={() => setShowColumnSettings((current) => !current)}>
                  <PortalIcon icon='fi fi-rr-settings-sliders' />
                </button>
              )}
              {globalActions.map((props, i) => (
                <CheckAccess
                  key={props?.content || props?.icon || i}
                  permission={props?.permission}
                  any={props?.any}
                  requireAssigned={props?.requireAssigned}>
                  <BWButton
                    style={{
                      marginLeft: '3px',
                    }}
                    iconOnly
                    {...props}
                  />
                </CheckAccess>
              ))}
            </div>
          </div>
        )}
        {columnPreferenceKey && showColumnSettings && (
          <div className='bw_column_settings'>
            <div className='bw_column_settings_title'>
              <span>Hiển thị cột</span>
              <button type='button' className='bw_table_inline_action' onClick={() => setShowColumnSettings(false)}>
                Đóng
              </button>
            </div>
            {dataColumns.map((column, index) => {
              if (column.hidden) return null;
              const key = getColumnPreferenceKey(column, index);
              const label = column.preferenceLabel
                ? normalizePortalText(column.preferenceLabel)
                : typeof column.header === 'string'
                  ? normalizePortalText(column.header)
                  : key;
              return (
                <label className='bw_column_settings_item' key={key}>
                  <input
                    type='checkbox'
                    checked={!tablePreferences.hiddenKeys.includes(key)}
                    disabled={column.lockedVisible}
                    onChange={() => handleToggleColumn(key)}
                  />
                  <span>{label}</span>
                </label>
              );
            })}
            <div className='bw_column_settings_footer'>
              <button type='button' className='bw_btn bw_btn_outline' onClick={handleResetColumns}>
                Khôi phục mặc định
              </button>
            </div>
          </div>
        )}
        <div className='bw_table_responsive'>
          <table className={`bw_table${columnPreferenceKey ? ' bw_resizable_table' : ''}`}>
            <thead>
              <tr>
                {!noSelect && (
                  <th className='bw_sticky bw_check_sticky'>
                    {Boolean(data.filter((o) => !hiddenRowSelect?.(o)).length) && (
                      <label className='bw_checkbox'>
                        {!uniqueSelect ? (
                          <>
                            <input type='checkbox' onChange={() => handleCheckAll()} checked={totalChecked} />
                            <span></span>
                          </>
                        ) : null}
                      </label>
                    )}
                  </th>
                )}
                {visibleLeadingColumns.map((column, index) => (
                  <th
                    style={column?.styleHeader}
                    key={`leading-${column?.key || column?.accessor || index}`}
                    className={column?.classNameHeader || ''}>
                    {typeof column?.header === 'string' ? normalizePortalText(column.header) : column?.header}
                  </th>
                ))}
                <th className='bw_sticky bw_no_sticky bw_text_center'>STT</th>

                {displayColumns.map((p, o) => (
                    <th
                      style={p?.styleHeader}
                      key={p?.key || p?.accessor || o}
                      className={p?.classNameHeader ? p?.classNameHeader : ''}>
                      {columnPreferenceKey ? (
                        <div className='bw_resizable_header'>
                          <span>{typeof p?.header === 'string' ? normalizePortalText(p?.header) : p?.header}</span>
                          <button
                            type='button'
                            className='bw_resize_handle'
                            title='Kéo để thay đổi độ rộng cột'
                            aria-label={`Thay đổi độ rộng cột ${p.preferenceLabel || (typeof p?.header === 'string' ? p.header : p.preferenceKey)}`}
                            onPointerDown={(event) => startColumnResize(event, p)}
                            onKeyDown={(event) => handleColumnResizeKeyDown(event, p)}
                          />
                        </div>
                      ) : typeof p?.header === 'string' ? (
                        normalizePortalText(p?.header)
                      ) : (
                        p?.header
                      )}
                    </th>
                  ))}
                {Boolean(rowAction.length) && <th className='bw_sticky bw_action_table bw_text_center'>Thao tác</th>}
              </tr>
            </thead>

            {loading ? (
              <tbody>
                {Array.from(Array(10).keys())?.map((rowIndex) => {
                  return (
                    <tr className='tr' key={`loading-row-${rowIndex}`}>
                      {Array.from(Array(colSpan).keys())?.map((columnIndex) => {
                        return (
                          <td className='td' key={`loading-cell-${rowIndex}-${columnIndex}`}>
                            <div className='loader'></div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            ) : (
              jsx_tbody()
            )}
          </table>
        </div>
        {shouldShowPaging && (
          <div className='bw_row bw_mt_2 bw_show_table_page'>
            <div className='bw_col_6'>
              {!hideRecordCount && (
                <p>
                  {numberedPagination ? `Hiển thị ${totalShowRecord}/${totalItems} chứng từ` : `Show ${totalShowRecord}/${totalItems} records`}
                </p>
              )}
            </div>
            <div className='bw_col_6 bw_flex bw_justify_content_right bw_align_items_center'>
              {numberedPagination ? (
                <Pagination
                  current={Number(page) || 1}
                  pageSize={Number(itemsPerPage) || 25}
                  total={Number(totalItems) || 0}
                  onChange={onChangePage}
                  showSizeChanger={false}
                  showLessItems={false}
                  showQuickJumper
                  locale={viVN.Pagination}
                  disabled={loading}
                />
              ) : <div className='bw_nav_table'>
                <button
                  type='button'
                  aria-label='Trang trước'
                  disabled={!(currentPage !== 1) || !totalPages}
                  onClick={() => {
                    onChangePage(parseInt(currentPage) - 1);
                  }}
                  className={totalPages && currentPage !== 1 ? 'bw_active' : ''}>
                  <PortalIcon icon='fi fi-rr-angle-small-left' />
                </button>
                <InputNumberStyled
                  aria-label='Trang hiện tại'
                  min={1}
                  onChange={(e) => {
                    setCurrentPage(e);
                  }}
                  onPressEnter={(e) => {
                    e.preventDefault();
                    onChangePage(currentPage);
                  }}
                  onBlur={() => handeChangePage()}
                  value={currentPage}
                  max={totalPages}
                />
                <span className='bw_all_page'>/ {totalPages}</span>
                <button
                  type='button'
                  aria-label='Trang sau'
                  disabled={parseInt(totalPages) === parseInt(currentPage)}
                  onClick={() => {
                    onChangePage(parseInt(currentPage) + 1);
                  }}
                  className={!(parseInt(totalPages) === parseInt(currentPage)) ? 'bw_active' : ''}>
                  <PortalIcon icon='fi fi-rr-angle-small-right' />
                </button>
              </div>}
            </div>
          </div>
        )}
      </Wrapper>
    </React.Fragment>
  );
};

DataTable.propTypes = {
  toolbarActions: PropTypes.node,
  emptyContent: PropTypes.node,
  numberedPagination: PropTypes.bool,
  /** Title of table */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /** Array of table's columns */
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  /** Columns rendered immediately after the select checkbox and before STT. */
  leadingColumns: PropTypes.arrayOf(PropTypes.shape({})),

  /** Array of table's data */
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  /** Decide if table is selectable */
  selectable: PropTypes.bool,
  selectedHidden: PropTypes.bool,
  onSelectionChange: PropTypes.func,

  /** Indicate table's loading state */
  loading: PropTypes.bool,

  /** Table actions */
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.number,
      icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
      content: PropTypes.string,
      onClick: PropTypes.func,
      color: PropTypes.string,
      globalAction: PropTypes.bool,
      disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      hidden: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      permission: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.func]),
      any: PropTypes.bool,
      requireAssigned: PropTypes.bool,
    }),
  ),

  /** on row click */
  onRowClick: PropTypes.func,

  /** number of pages (controlled pagination) */
  pageCount: PropTypes.number,

  /** number of rows (controlled pagination) */
  totalCounts: PropTypes.number,

  /** No Paging flag */
  noPaging: PropTypes.bool,
  plain: PropTypes.bool,
  columnPreferenceKey: PropTypes.string,
};

export default DataTable;
